import React from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, MenuItem } from '@mui/material';
import { useMaterialsStore } from '@components/Stores/MaterialsStore';
import { useFormulasPrices } from '../../../hooks/useFormulasPrices';
import { useUpdateDerivedMaterial } from '../../../hooks/useBaseHedgeMaterials';
import { DerivedHedgeMaterial } from '../../../services/baseHedgeMaterialsService';

interface Props {
  open: boolean;
  onClose: () => void;
  derived: DerivedHedgeMaterial | null;
}

type Mode = 'live' | 'fixed';

const fmtDate = (iso?: string | null) => {
  if (!iso) return '';
  const [y, m, d] = String(iso).slice(0, 10).split('-');
  return `${d}.${m}.${y}`;
};

/** Edit how a derived material's ratio is set: a live daily ratio instrument, or a fixed value. */
export const EditDerivedRatioModal: React.FC<Props> = ({ open, onClose, derived }) => {
  const { materialsMap } = useMaterialsStore();
  const { data: pricesData } = useFormulasPrices();
  const updateMutation = useUpdateDerivedMaterial();

  const ratioMaterials = React.useMemo(
    () => Object.values(materialsMap).filter(m => m.contractType === 'ratio'),
    [materialsMap],
  );
  const live = (code?: string | null) => (code ? pricesData?.pricesMap?.[code] : undefined);

  const [mode, setMode] = React.useState<Mode>('fixed');
  const [ratioCode, setRatioCode] = React.useState('');
  const [fixedValue, setFixedValue] = React.useState('');

  // Initialise from the material's current setting every time the dialog opens.
  React.useEffect(() => {
    if (!open || !derived) return;
    const linked = derived.linked_ratio_code || '';
    setMode(linked ? 'live' : 'fixed');
    setRatioCode(linked);
    const current = live(linked)?.price;
    setFixedValue(String(Number(current && current > 0 ? current : derived.price_ratio).toFixed(2)));
    updateMutation.reset();
  }, [open, derived]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!derived) return null;

  const selectedLive = live(ratioCode);
  const fixedNum = parseFloat(fixedValue);
  const canSave = mode === 'live' ? !!ratioCode : !Number.isNaN(fixedNum) && fixedNum > 0;

  const save = () => {
    if (!canSave) return;
    updateMutation.mutate(
      {
        derivedMaterialId: derived.id,
        payload: mode === 'live'
          // Keep the current live value as the stored fallback (used only if the instrument
          // has never had a price).
          ? { linked_ratio_code: ratioCode, price_ratio: selectedLive?.price && selectedLive.price > 0 ? selectedLive.price : Number(derived.price_ratio) }
          : { linked_ratio_code: null, price_ratio: fixedNum },
      },
      { onSuccess: onClose },
    );
  };

  const option = (value: Mode, title: string, desc: string) => (
    <button
      type="button"
      onClick={() => setMode(value)}
      className="text-left rounded-lg p-3 w-full"
      style={{
        border: `2px solid ${mode === value ? '#2563eb' : '#e5e7eb'}`,
        background: mode === value ? '#eff6ff' : '#fff',
        cursor: 'pointer',
      }}
    >
      <div className="flex items-center gap-2">
        <span style={{ width: 14, height: 14, borderRadius: 999, border: `2px solid ${mode === value ? '#2563eb' : '#cbd5e1'}`, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
          {mode === value && <span style={{ width: 6, height: 6, borderRadius: 999, background: '#2563eb' }} />}
        </span>
        <span className="text-sm font-semibold text-gray-900">{title}</span>
      </div>
      <div className="text-xs text-gray-600 mt-1" style={{ paddingLeft: 22 }}>{desc}</div>
    </button>
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Ratio for {derived.formula_name}</DialogTitle>
      <DialogContent>
        <div className="space-y-4 pt-1">
          <div className="text-xs text-gray-600">
            The ratio converts this material to base-material tons (e.g. tons of beans per ton of butter).
          </div>

          <div className="grid grid-cols-2 gap-3">
            {option('live', 'Live daily ratio', 'Follows a ratio instrument (e.g. LSEG) every day. If a day has no new value, the latest known value is used.')}
            {option('fixed', 'Fixed ratio', 'A constant you set. Stays the same until you change it.')}
          </div>

          {mode === 'live' ? (
            <>
              <TextField
                select
                label="Ratio instrument"
                value={ratioCode}
                onChange={e => setRatioCode(e.target.value)}
                fullWidth
                size="small"
              >
                {ratioMaterials.length === 0 && <MenuItem disabled>No ratio instruments available</MenuItem>}
                {ratioMaterials.map(m => {
                  const p = live(m.id);
                  return (
                    <MenuItem key={m.id} value={m.id}>
                      <span className="flex items-center justify-between w-full gap-3">
                        <span>{m.name}</span>
                        <span className="text-xs text-gray-500">
                          {p?.price ? `${p.price.toFixed(2)} · ${fmtDate(p.priceDate)}` : 'no price yet'}
                        </span>
                      </span>
                    </MenuItem>
                  );
                })}
              </TextField>
              {ratioCode && (
                <div className="text-sm rounded p-2" style={{ background: selectedLive?.price ? '#eff6ff' : '#fffbeb' }}>
                  {selectedLive?.price
                    ? <>Current value: <strong>{selectedLive.price.toFixed(2)}</strong> <span className="text-xs text-gray-500">(latest price, {fmtDate(selectedLive.priceDate)})</span></>
                    : <>This instrument has no price yet. Until it does, the stored value <strong>{Number(derived.price_ratio).toFixed(2)}</strong> is used.</>}
                </div>
              )}
            </>
          ) : (
            <TextField
              label="Fixed ratio"
              type="number"
              value={fixedValue}
              onChange={e => setFixedValue(e.target.value)}
              fullWidth
              size="small"
              inputProps={{ step: 0.01, min: 0.01 }}
              helperText={derived.linked_ratio_code && live(derived.linked_ratio_code)?.price
                ? `Today's live value of ${derived.linked_ratio_name ?? derived.linked_ratio_code}: ${live(derived.linked_ratio_code)!.price.toFixed(2)}`
                : 'Base-material tons per 1 ton of this material'}
            />
          )}

          <div className="text-xs text-gray-500 border-t pt-3">
            Applies to the whole account: hedge allocation, policies and the coverage tables. Everything is recalculated automatically after saving.
          </div>
          {updateMutation.isError && <div className="text-xs text-red-600">Saving failed. Please try again.</div>}
        </div>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit">Cancel</Button>
        <Button onClick={save} variant="contained" disabled={!canSave || updateMutation.isLoading}>
          {updateMutation.isLoading ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
